// Synchronisation (H3) : file de cet appareil avec l'état de chaque enregistrement (enregistré sur cet appareil,
// en attente d'envoi, envoyé en attente d'accusé, synchronisé, conflit à résoudre, refusé avec son motif), envoi
// manuel, purge du stockage détectée ; écran de résolution des conflits pour les validateurs (les deux versions
// côte à côte, version retenue et motif tracés).

import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { LIBELLES_TYPES_OPERATIONS } from '@carburanttrack/shared/logistique';
import { api, messageErreur } from '../../api/client';
import { Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import { useHorsLigne } from '../../horsligne/contexte';
import { ETATS_OPERATION, LIBELLES_ETATS_OPERATION, type EtatOperation, type OperationStockee } from '../../horsligne/file';
import { nomProduit } from './Station';

const TONS: Record<EtatOperation, string> = { enregistre_local: 'ambre', en_attente_envoi: 'ambre', envoye: 'ambre', synchronise: 'vert', conflit: 'rouge', refuse: 'rouge' };
const libelleType = (t: string) => LIBELLES_TYPES_OPERATIONS[t] ?? t.replace(/_/g, ' ');

export function PageSynchronisation() {
  const { synchroniseur, etat } = useHorsLigne();
  const [operations, setOperations] = useState<(OperationStockee & { libelle: string | null })[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  useEffect(() => {
    let actif = true;
    synchroniseur
      .operations()
      .then((o) => actif && setOperations(o.sort((a, b) => b.horodatageLocal.localeCompare(a.horodatageLocal))))
      .catch((e: unknown) => actif && setErreur(e));
    return () => {
      actif = false;
    };
  }, [synchroniseur, etat.compteurs, etat.derniereSynchro]);
  const envoyer = useMutation({ mutationFn: () => synchroniseur.synchroniser() });
  const actualiser = useMutation({ mutationFn: () => synchroniseur.actualiserPaquet() });
  const enAttente = etat.compteurs.enregistre_local + etat.compteurs.en_attente_envoi + etat.compteurs.envoye;
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Synchronisation de cet appareil</h1>
          <p className="attenue">Un enregistrement n’est « synchronisé » qu’après l’accusé du serveur. Les saisies faites hors connexion conservent leur heure d’événement d’origine.</p>
        </div>
        <div className="actions">
          <button type="button" className="bouton primaire" disabled={!etat.enLigne || etat.enCours || envoyer.isPending || enAttente === 0} onClick={() => envoyer.mutate()}>
            {etat.enCours ? 'Envoi en cours' : 'Envoyer maintenant'}
          </button>
          <button type="button" className="bouton" disabled={!etat.enLigne || actualiser.isPending} onClick={() => actualiser.mutate()}>
            Actualiser les données de l’appareil
          </button>
        </div>
      </div>
      {etat.perdus.length > 0 && (
        <Message type="avertissement">
          {etat.perdus.length} enregistrement{etat.perdus.length > 1 ? 's' : ''} locaux ont été effacés par le système de l’appareil avant leur envoi. Ressaisissez-les si le serveur ne les a pas reçus.{' '}
          <button type="button" className="bouton discret" onClick={() => void synchroniseur.prendreActeDeLaPurge()}>
            J’en prends acte
          </button>
        </Message>
      )}
      {etat.sessionExpiree && <Message type="avertissement">La session locale a expiré (plus de 24 h sans confirmation du serveur) : reconnectez-vous pour envoyer vos enregistrements.</Message>}
      <div className="compteurs" role="list" aria-label="État de la file">
        {ETATS_OPERATION.map((e) => (
          <div role="listitem" key={e}>
            <b className={(e === 'conflit' || e === 'refuse') && etat.compteurs[e] ? 'ambre' : ''}>{etat.compteurs[e]}</b>
            {LIBELLES_ETATS_OPERATION[e].toLowerCase()}
          </div>
        ))}
      </div>
      <dl className="liste-definitions carte-bloc">
        <dt>Réseau</dt>
        <dd>{etat.enLigne ? 'connecté' : 'hors connexion'}</dd>
        <dt>Dernière synchronisation</dt>
        <dd>{etat.derniereSynchro ? dateHeure(etat.derniereSynchro) : 'jamais sur cet appareil'}</dd>
        <dt>Données de l’appareil</dt>
        <dd>{etat.paquetVersion ? `version ${etat.paquetVersion.slice(0, 12)}, enregistrée ${dateHeure(etat.paquetLe)}` : 'non chargées'}</dd>
        {etat.derniereErreur && (
          <>
            <dt>Dernier envoi interrompu</dt>
            <dd>{etat.derniereErreur}</dd>
          </>
        )}
      </dl>
      {envoyer.data && (
        <Message type={envoyer.data.interrompu ? 'avertissement' : 'succes'}>
          {envoyer.data.interrompu
            ? `Envoi interrompu : ${envoyer.data.interrompu}. Les enregistrements restent en attente d’envoi.`
            : `${envoyer.data.envoyees} envoyé${envoyer.data.envoyees > 1 ? 's' : ''} : ${envoyer.data.synchronisees} synchronisé${envoyer.data.synchronisees > 1 ? 's' : ''}, ${envoyer.data.conflits} en conflit, ${envoyer.data.refusees} refusé${envoyer.data.refusees > 1 ? 's' : ''}.`}
        </Message>
      )}
      {actualiser.error && <Message type="erreur">{messageErreur(actualiser.error)}</Message>}
      <section className="carte-bloc" aria-labelledby="titre-file">
        <h2 id="titre-file">Enregistrements</h2>
        {Boolean(erreur) && <MessageErreur erreur={erreur} titre="File illisible" />}
        {!operations && !erreur && <Squelette lignes={3} />}
        {operations && operations.length === 0 && <Vide>Aucun enregistrement sur cet appareil.</Vide>}
        {operations && operations.length > 0 && (
          <ul className="liste-lignes">
            {operations.map((o) => (
              <li key={o.identifiantClient}>
                <span>
                  <b>{o.libelle ?? libelleType(o.type)}</b>
                  <small>
                    {libelleType(o.type)}, événement {dateHeure(o.horodatageEvenement)}, enregistré {dateHeure(o.horodatageLocal)}
                    {o.accuseLe ? `, accusé ${dateHeure(o.accuseLe)}` : ''}
                  </small>
                  {o.motif && <small>Motif : {o.motif}</small>}
                </span>
                <span className={`etiquette ${TONS[o.etat]}`}>{LIBELLES_ETATS_OPERATION[o.etat]}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="attenue">Les enregistrements synchronisés et refusés sont retirés de l’appareil après 30 jours. Un conflit est tranché par un validateur ; il reste en conflit ici jusqu’à ce que la décision soit reçue à la synchronisation suivante, avec son motif. Un enregistrement devenu illisible (application fermée avant l’envoi) est refusé avec ce motif : saisissez-le de nouveau.</p>
      </section>
    </div>
  );
}

// --- Conflits (validateurs) --------------------------------------------------------------------------------------

interface Conflit {
  id: string;
  identifiantClient: string;
  utilisateurId: string;
  type: string;
  etat: string;
  motif: string | null;
  horodatageEvenement: string;
  horodatageLocal: string;
  horodatageReception: string;
  objet: { type: string; id: string } | null;
  versions: { client: Record<string, unknown> | null; serveur: Record<string, unknown> | null } | null;
}

const LIBELLES_CHAMPS: Record<string, string> = {
  quantite: 'Quantité',
  volumeBrut: 'Volume',
  horodatage: 'Heure de l’événement',
  horodatageMesure: 'Heure de la mesure',
  type: 'Type',
  lieu: 'Lieu',
  statutStation: 'Statut de la station',
  etatOrdre: 'État de la livraison',
  reference: 'Référence',
  etat: 'État',
  creeLe: 'Créé le',
  produit: 'Produit',
  produitCode: 'Produit',
  dateSouhaitee: 'Date souhaitée',
  urgence: 'Urgence',
  statut: 'Statut',
  commentaire: 'Commentaire',
};

/** Valeur lisible : date, volume, produit, ou code dont les soulignés deviennent des espaces. */
function valeurLisible(k: string, x: string | number | boolean): string {
  if (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(x)) return dateHeure(x);
  if (CHAMPS_VOLUME.has(k) && /^-?\d+(\.\d+)?$/.test(String(x))) return `${formaterNombre(Number(x))} L`;
  if (k === 'produit' || k === 'produitCode') return nomProduit(String(x));
  return String(x).replace(/_/g, ' ');
}

const CHAMPS_VOLUME = new Set(['quantite', 'volumeBrut']);

function Version({ titre, v }: { titre: string; v: Record<string, unknown> | null }) {
  return (
    <div className="carte-bloc">
      <h3>{titre}</h3>
      {!v ? (
        <p className="attenue">indisponible</p>
      ) : (
        <dl className="liste-definitions">
          {Object.entries(v)
            // Identifiants techniques écartés : seules les valeurs saisies sont comparées.
            .filter(([k, x]) => x !== null && typeof x !== 'object' && LIBELLES_CHAMPS[k] !== undefined)
            .map(([k, x]) => (
              <Fragment key={k}>
                <dt>{LIBELLES_CHAMPS[k]}</dt>
                <dd>{valeurLisible(k, x as string | number | boolean)}</dd>
              </Fragment>
            ))}
        </dl>
      )}
    </div>
  );
}

export function PageConflits() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['conflits-synchro'], queryFn: async () => (await api<Conflit[]>('/api/v1/sync/conflits')).donnees });
  const [choix, setChoix] = useState<Record<string, { version: 'client' | 'serveur' | ''; motif: string }>>({});
  const resoudre = useMutation({
    mutationFn: async (id: string) => api(`/api/v1/sync/conflits/${id}/resoudre`, { corps: { versionRetenue: choix[id]!.version, motif: choix[id]!.motif } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['conflits-synchro'] }),
  });
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Conflits de synchronisation</h1>
          <p className="attenue">Un même objet a été modifié hors connexion par deux personnes. Les deux versions sont conservées ; retenez-en une avec un motif. La décision est inscrite au journal d’audit.</p>
        </div>
      </div>
      {q.isLoading && <Squelette lignes={4} />}
      {q.error && <MessageErreur erreur={q.error} titre="Conflits indisponibles" />}
      {q.data && q.data.length === 0 && <Vide>Aucun conflit à résoudre.</Vide>}
      {q.data?.map((c) => {
        const ch = choix[c.id] ?? { version: '', motif: '' };
        return (
          <section className="carte-bloc" key={c.id} aria-labelledby={`conflit-${c.id}`}>
            <div className="carte-bloc-tete">
              <h2 id={`conflit-${c.id}`}>{libelleType(c.type)}</h2>
              <span className="attenue">
                Saisi {dateHeure(c.horodatageEvenement)}, reçu {dateHeure(c.horodatageReception)}
              </span>
            </div>
            <p>{c.motif ?? 'Conflit avec une autre saisie'}</p>
            <div className="grille-2">
              <Version titre="Version de l’appareil" v={c.versions?.client ?? null} />
              <Version titre="Version enregistrée sur le serveur" v={c.versions?.serveur ?? null} />
            </div>
            <form
              className="formulaire-ligne espace-haut"
              onSubmit={(e) => {
                e.preventDefault();
                resoudre.mutate(c.id);
              }}
            >
              <div className="champ">
                <label htmlFor={`v-${c.id}`}>Version retenue</label>
                <select id={`v-${c.id}`} value={ch.version} onChange={(e) => setChoix({ ...choix, [c.id]: { ...ch, version: e.target.value as 'client' | 'serveur' } })} required>
                  <option value="">Choisir</option>
                  <option value="client">Version de l’appareil</option>
                  <option value="serveur">Version du serveur</option>
                </select>
              </div>
              <div className="champ">
                <label htmlFor={`m-${c.id}`}>Motif</label>
                <input id={`m-${c.id}`} value={ch.motif} onChange={(e) => setChoix({ ...choix, [c.id]: { ...ch, motif: e.target.value } })} minLength={3} maxLength={1000} required />
              </div>
              <div className="actions">
                <button type="submit" className="bouton primaire" disabled={resoudre.isPending || !ch.version || ch.motif.trim().length < 3}>
                  Résoudre
                </button>
              </div>
              {resoudre.error && resoudre.variables === c.id && <Message type="erreur">{messageErreur(resoudre.error)}</Message>}
            </form>
          </section>
        );
      })}
    </div>
  );
}
